/**
 * Purpose:
 *   Queue and apply client-side history state across Next.js route transitions.
 *
 * Notes:
 *   Next.js router navigation does not expose a first-class state payload, so we temporarily
 *   stash the next state in sessionStorage and apply it on the destination page.
 */

const NEXT_HISTORY_STATE_STORAGE_KEY = 'next-history-state';

type THistoryStatePayload = Record<string, unknown>;

function canUseBrowserState() {
  return typeof window !== 'undefined' && typeof sessionStorage !== 'undefined';
}

function readCurrentHistoryState(): THistoryStatePayload {
  if (typeof window === 'undefined') return {};
  const current = window.history.state;

  return current && typeof current === 'object' ? { ...current } : {};
}

export function queueNextHistoryState(state?: THistoryStatePayload) {
  if (!canUseBrowserState()) return;

  if (!state || Object.keys(state).length === 0) {
    sessionStorage.removeItem(NEXT_HISTORY_STATE_STORAGE_KEY);
    return;
  }

  sessionStorage.setItem(NEXT_HISTORY_STATE_STORAGE_KEY, JSON.stringify(state));
}

export function applyQueuedHistoryState(): THistoryStatePayload {
  if (!canUseBrowserState()) return {};

  const raw = sessionStorage.getItem(NEXT_HISTORY_STATE_STORAGE_KEY);
  if (!raw) {
    return readCurrentHistoryState();
  }

  sessionStorage.removeItem(NEXT_HISTORY_STATE_STORAGE_KEY);

  try {
    const parsed = JSON.parse(raw) as THistoryStatePayload;
    const nextState = { ...readCurrentHistoryState(), ...parsed };

    window.history.replaceState(nextState, '', window.location.href);
    return nextState;
  } catch {
    return readCurrentHistoryState();
  }
}
